import { useStore } from 'vuex'
import { useAdmin } from './useAdmin'
import { decodeJwtPermissions } from '@/utils/jwt'

/**
 * Algumas telas antigas usam uma chave de permissão que colide de nome com uma
 * chave REAL do WootDash que significa outra coisa (ex: "templates.view" no
 * front sempre quis dizer "Templates de WhatsApp", mas no backend essa chave é de
 * Respostas Prontas — o feature de WhatsApp é "waTemplates.view"). Mapeia pra chave
 * real antes de checar a claim do JWT. Chaves que já batem (teams.*, macros.*,
 * sla.*, etiquetas.*, contacts.*, avaliacoes.view, resumo.view...) não precisam
 * de entrada aqui.
 */
const PERMISSION_KEY_ALIASES: Record<string, string> = {
  'templates.view': 'waTemplates.view',
  'templates.create': 'waTemplates.create',
  'templates.edit': 'waTemplates.edit',
  'templates.delete': 'waTemplates.delete',
  'agentes.view': 'dashboard.viewAgents',
}

/**
 * Returns `can(permKey)` — checks whether the current logged-in user has the
 * given permission.
 *
 * Fonte de verdade agora é a claim "perm" do JWT do WootDash (RBAC normalizado
 * no backend, config.role_permissions) — não mais o admin_config local em
 * localStorage, que é o blob de permissão do sistema antigo (chaveado por id
 * numérico do Chatwoot) e nunca vai saber de um agente que só existe no
 * WootDash. useAdmin/hasPermission fica só como fallback pra chave que ainda
 * não existe no catálogo novo (nenhuma tela migrada precisa disso hoje).
 */
/**
 * A mesma checagem de can(), sem precisar de componente (router.beforeEach): claim "perm" do JWT
 * do WootDash primeiro; perfil antigo em cache só como reserva pra chave que não existe no catálogo.
 */
export function userCan(authUser: { id?: number; wootdash_token?: string | null } | null | undefined, teamIds: number[], permKey: string): boolean {
  const token = authUser?.wootdash_token
  if (token) {
    const realKey = PERMISSION_KEY_ALIASES[permKey] || permKey
    if (decodeJwtPermissions(token).includes(realKey)) return true
  }
  if (!authUser?.id) return false
  return useAdmin().hasPermission(authUser.id, teamIds, permKey)
}

export function usePermission() {
  const store = useStore()
  const { hasPermission, getProfileTeamIds } = useAdmin()

  function can(permKey: string): boolean {
    const token = store.getters.authUser?.wootdash_token
    if (token) {
      const realKey = PERMISSION_KEY_ALIASES[permKey] || permKey
      if (decodeJwtPermissions(token).includes(realKey)) return true
    }
    const user  = store.getters.authUser
    if (!user?.id) return false
    const teamIds: number[] = store.getters.userTeamIds ?? []
    return hasPermission(user.id, teamIds, permKey)
  }

  /**
   * IDs de times que o usuário atual pode escolher pra reatribuir — sempre
   * null (sem restrição) de propósito, ver getProfileTeamIds em useAdmin.ts.
   */
  function allowedTeamIds(): number[] | null {
    return getProfileTeamIds()
  }

  return { can, allowedTeamIds }
}
